import { entryKeys, tagKeys, type Entry } from '@taler/api-client'
import { addMoney, type LocalDate, type Money, negateMoney } from '@taler/domain'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, reactive, ref, type Ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/app/api'
import { createRow, createSplit, type FlowKind, type RowDraft, toPostEntryInput } from '../internal/draft.ts'
import {
  type ExtensionMatch,
  matchExtension,
  type RegisteredExtension,
  rowView,
  type RowView,
} from '../internal/extension.ts'
import { prefersReducedMotion } from '../internal/motion.ts'
import { problemDetail } from '../internal/problem.ts'
import { isTouched, resolveRow, type RowResolution } from '../internal/row-status.ts'
import type { Target } from '../internal/targets.ts'

export interface UseEntryBatchOptions {
  readonly ledgerId: () => string | undefined
  readonly kind: () => FlowKind
  readonly targetsById: () => ReadonlyMap<string, Target>
  /** Accounts offered from another ledger (AGENTS.md's cross-ledger picker) - never merged
   * into `targetsById`, since a fixture id can coincide across ledgers; resolved by
   * `(id, ledgerId)` instead wherever a row's own account or counter was picked foreign. */
  readonly foreignTargets?: () => readonly Target[]
  readonly today: () => LocalDate
  /** In priority order: the first that recognises a row owns it. */
  readonly extensions?: () => readonly RegisteredExtension[]
}

export interface AccountDelta {
  readonly accountId: string
  readonly delta: Money
}

export interface RowOutcome {
  /** Why this row's own step failed; the rest of the batch is unaffected. */
  readonly error: string | undefined
  /** Set when the entry posted but its attached record did not: retry attaches, never re-posts. */
  readonly postedEntry: Entry | undefined
}

export interface BatchCounts {
  readonly ready: number
  readonly needsDetails: number
  readonly blocked: number
  readonly incomplete: number
}

export interface UseEntryBatch {
  readonly rows: Ref<RowDraft[]>
  readonly views: ComputedRef<ReadonlyMap<string, RowView>>
  readonly resolutions: ComputedRef<ReadonlyMap<string, RowResolution>>
  readonly outcomes: ReadonlyMap<string, RowOutcome>
  readonly counts: ComputedRef<BatchCounts>
  /** Every touched row is ready: the batch waits for the last one rather than leaving it behind. */
  readonly canSubmit: ComputedRef<boolean>
  readonly runningDeltas: ComputedRef<readonly AccountDelta[]>
  readonly isSubmitting: ComputedRef<boolean>
  readonly submitError: ComputedRef<Error | null>
  /** The server's own reason, when it sent one - undefined for a network/unknown failure. */
  readonly submitErrorDetail: ComputedRef<string | undefined>
  /** Row ids from the batch that just posted successfully - drives the "Posted" motion
   * moment (AGENTS.md §Motion) and is cleared the moment a new row starts. */
  readonly justPosted: Ref<readonly string[]>
  /** Appends an empty row carrying the last row's date and account. */
  addRow(): void
  addSplit(rowId: string): void
  removeSplit(rowId: string, splitId: string): void
  removeRow(rowId: string): void
  /** Commits a ready row: if it is the last row, a new one is appended carrying its
   * date and account forward (AGENTS.md: re-typing what you just typed is what makes
   * bulk entry miserable). A no-op on any other row - Enter just moves focus there. */
  commitRow(rowId: string): void
  setExtensionDraft(rowId: string, draft: unknown): void
  retryAttach(rowId: string): Promise<void>
  submit(): Promise<void>
  reset(): void
}

interface Slot {
  readonly key: string
  readonly draft: unknown
}

export function useEntryBatch(options: UseEntryBatchOptions): UseEntryBatch {
  const api = useApi()
  const queryClient = useQueryClient()
  const { t } = useI18n()

  const rows = ref<RowDraft[]>([createRow(options.today(), undefined)]) as Ref<RowDraft[]>
  const justPosted = ref<readonly string[]>([])
  const slots = reactive(new Map<string, Slot>())
  const outcomes = reactive(new Map<string, RowOutcome>())

  function targets(): ReadonlyMap<string, Target> {
    return options.targetsById()
  }

  const views = computed<ReadonlyMap<string, RowView>>(
    () =>
      new Map(
        rows.value.map((row) => [
          row.id,
          rowView(options.kind(), row, targets(), options.foreignTargets?.() ?? []),
        ]),
      ),
  )

  const matches = computed(() => {
    const extensions = options.extensions?.() ?? []
    const out = new Map<string, NonNullable<ReturnType<typeof matchExtension>>>()
    for (const [rowId, view] of views.value) {
      const found = matchExtension(extensions, view)
      if (found !== undefined) out.set(rowId, found)
    }
    return out
  })

  function slotKey(extension: RegisteredExtension, match: ExtensionMatch): string {
    return `${extension.id}:${match.key}`
  }

  function draftFor(rowId: string, view: RowView): unknown {
    const found = matches.value.get(rowId)
    if (found === undefined) return undefined
    const slot = slots.get(rowId)
    if (slot?.key === slotKey(found.extension, found.match)) return slot.draft
    return found.extension.initialDraft(found.match, view)
  }

  watch(
    matches,
    (current) => {
      for (const rowId of [...slots.keys()]) {
        if (!current.has(rowId)) slots.delete(rowId)
      }
      for (const [rowId, found] of current) {
        const key = slotKey(found.extension, found.match)
        if (slots.get(rowId)?.key === key) continue
        slots.set(rowId, { key, draft: found.extension.initialDraft(found.match, views.value.get(rowId)!) })
      }
    },
    { immediate: true },
  )

  const resolutions = computed<ReadonlyMap<string, RowResolution>>(() => {
    const out = new Map<string, RowResolution>()
    for (const [rowId, view] of views.value) {
      const found = matches.value.get(rowId)
      out.set(
        rowId,
        resolveRow(
          view,
          found?.extension,
          found?.match,
          draftFor(rowId, view),
          t('entries.extension.splitsNotCarried', { label: found?.match.label ?? '' }),
        ),
      )
    }
    return out
  })

  /** Rows still waiting on this batch: touched, and not already posted. */
  const pendingRows = computed(() =>
    rows.value.filter(
      (row) =>
        isTouched(row) &&
        !justPosted.value.includes(row.id) &&
        outcomes.get(row.id)?.postedEntry === undefined,
    ),
  )

  const counts = computed<BatchCounts>(() => {
    const tally = { ready: 0, needsDetails: 0, blocked: 0, incomplete: 0 }
    for (const row of pendingRows.value) {
      const status = resolutions.value.get(row.id)?.status
      if (status === 'ready') tally.ready += 1
      else if (status === 'needs-details') tally.needsDetails += 1
      else if (status === 'blocked') tally.blocked += 1
      else tally.incomplete += 1
    }
    return tally
  })

  const canSubmit = computed(
    () =>
      counts.value.ready > 0 &&
      counts.value.needsDetails + counts.value.blocked + counts.value.incomplete === 0,
  )

  function addRow(): void {
    const last = rows.value.at(-1)
    rows.value.push(createRow(last?.date ?? options.today(), last?.accountId, last?.accountLedgerId))
  }

  function addSplit(rowId: string): void {
    const row = rows.value.find((candidate) => candidate.id === rowId)
    row?.splits.push(createSplit())
  }

  function removeSplit(rowId: string, splitId: string): void {
    const row = rows.value.find((candidate) => candidate.id === rowId)
    if (row === undefined || row.splits.length <= 1) return
    row.splits = row.splits.filter((split) => split.id !== splitId)
  }

  function removeRow(rowId: string): void {
    if (rows.value.length <= 1) return
    rows.value = rows.value.filter((row) => row.id !== rowId)
    outcomes.delete(rowId)
  }

  function commitRow(rowId: string): void {
    const index = rows.value.findIndex((row) => row.id === rowId)
    if (index === -1) return
    const row = rows.value[index]!
    if (resolutions.value.get(rowId)?.status !== 'ready') return
    if (index === rows.value.length - 1) {
      rows.value.push(createRow(row.date, row.accountId, row.accountLedgerId))
    }
  }

  function setExtensionDraft(rowId: string, draft: unknown): void {
    const found = matches.value.get(rowId)
    if (found === undefined) return
    slots.set(rowId, { key: slotKey(found.extension, found.match), draft })
  }

  const runningDeltas = computed<readonly AccountDelta[]>(() => {
    const byAccount = new Map<string, Money>()
    for (const row of rows.value) {
      const status = resolutions.value.get(row.id)?.status
      if (row.accountId === undefined || (status !== 'ready' && status !== 'needs-details')) continue
      const total = views.value.get(row.id)?.total
      if (total === undefined) continue
      const delta = options.kind() === 'INCOME' ? total : negateMoney(total)
      const existing = byAccount.get(row.accountId)
      byAccount.set(row.accountId, existing === undefined ? delta : addMoney(existing, delta))
    }
    return [...byAccount.entries()].map(([accountId, delta]) => ({ accountId, delta }))
  })

  function failureText(error: unknown): string {
    return problemDetail(error instanceof Error ? error : null) ?? t('entries.submitErrorGeneric')
  }

  function invalidateJournal(): void {
    for (const key of entryKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
    void queryClient.invalidateQueries({ queryKey: tagKeys.list(options.ledgerId() ?? '') })
  }

  // The posted rows stay in place, tinted, through the "Posted" motion moment
  // (AGENTS.md §Motion) - removing them immediately would cut the animation off
  // before it plays. An editable row always exists in the meantime.
  function markPosted(rowIds: readonly string[]): void {
    if (rowIds.length === 0) return
    justPosted.value = [...justPosted.value, ...rowIds]
    const posted = new Set(rowIds)
    const lastPosted = [...rows.value].reverse().find((row) => posted.has(row.id))
    if (rows.value.every((row) => justPosted.value.includes(row.id))) {
      rows.value = [
        ...rows.value,
        createRow(lastPosted?.date ?? options.today(), lastPosted?.accountId, lastPosted?.accountLedgerId),
      ]
    }
    invalidateJournal()
    setTimeout(
      () => {
        rows.value = rows.value.filter((row) => !posted.has(row.id))
        justPosted.value = justPosted.value.filter((id) => !posted.has(id))
        for (const id of posted) outcomes.delete(id)
      },
      prefersReducedMotion() ? 300 : 1000,
    )
  }

  const submitMutation = useMutation({
    mutationFn: async () => {
      const kind = options.kind()
      if (!canSubmit.value) return [] as readonly string[]
      const plan = pendingRows.value.map((row) => {
        const resolution = resolutions.value.get(row.id)!
        return { row, view: views.value.get(row.id)!, resolution, draft: resolution.draft }
      })
      for (const step of plan) outcomes.delete(step.row.id)

      const batched = plan.filter((step) => step.resolution.extension?.submit.shape !== 'command')
      const inputs = batched.map((step) => {
        const submit = step.resolution.extension?.submit
        return submit?.shape === 'postings'
          ? submit.build(step.resolution.match as never, step.draft as never, step.view)
          : toPostEntryInput(kind, step.row, targets())!
      })
      const created = inputs.length === 0 ? [] : await api.entries.batchCreate(options.ledgerId()!, inputs)
      const done: string[] = []

      for (const step of plan) {
        const submit = step.resolution.extension?.submit
        if (submit?.shape !== 'command') continue
        try {
          const next = await submit.run(step.resolution.match as never, step.draft as never, step.view)
          setExtensionDraft(step.row.id, next)
          done.push(step.row.id)
        } catch (error) {
          outcomes.set(step.row.id, { error: failureText(error), postedEntry: undefined })
        }
      }

      for (const [index, step] of batched.entries()) {
        const submit = step.resolution.extension?.submit
        if (submit?.shape !== 'attach') {
          done.push(step.row.id)
          continue
        }
        const entry = created[index]!
        try {
          await submit.run(entry, step.resolution.match as never, step.draft as never, step.view)
          done.push(step.row.id)
        } catch (error) {
          outcomes.set(step.row.id, { error: failureText(error), postedEntry: entry })
        }
      }

      if (created.length > 0 && done.length === 0) invalidateJournal()
      return done
    },
    onSuccess: (done) => markPosted(done),
  })

  async function retryAttach(rowId: string): Promise<void> {
    const entry = outcomes.get(rowId)?.postedEntry
    const resolution = resolutions.value.get(rowId)
    const view = views.value.get(rowId)
    const submit = resolution?.extension?.submit
    if (entry === undefined || view === undefined || submit?.shape !== 'attach') return
    try {
      await submit.run(entry, resolution!.match as never, resolution!.draft as never, view)
      outcomes.set(rowId, { error: undefined, postedEntry: entry })
      markPosted([rowId])
    } catch (error) {
      outcomes.set(rowId, { error: failureText(error), postedEntry: entry })
    }
  }

  return {
    rows,
    views,
    resolutions,
    outcomes,
    counts,
    canSubmit,
    runningDeltas,
    isSubmitting: computed(() => submitMutation.isPending.value),
    submitError: computed(() => submitMutation.error.value),
    submitErrorDetail: computed(() => problemDetail(submitMutation.error.value)),
    justPosted,
    addRow,
    addSplit,
    removeSplit,
    removeRow,
    commitRow,
    setExtensionDraft,
    retryAttach,
    // The mutation's own `error` ref is what the UI reads; this promise is not
    // awaited by any caller, so a rejection here must not become an unhandled one.
    submit: () =>
      submitMutation.mutateAsync().then(
        () => undefined,
        () => undefined,
      ),
    reset: () => {
      rows.value = [createRow(options.today(), undefined)]
      justPosted.value = []
      slots.clear()
      outcomes.clear()
      submitMutation.reset()
    },
  }
}
